import { Component } from 'react';

import { LoginDialogMode } from '../../components/ui/login/login-modal.component';
import LoginComponent from '../../components/ui/login/login.component';
import { lazyInject } from '../../context/module';
import { ApiConfig } from '../../services/api-config';
import { LightHttp } from '../../services/light-http.service';
import { LoginService } from '../../services/login.service';
import { OfflineTaskService } from '../../services/offline-task.service';
import { ScriptLoadService } from '../../services/script-load.service';
import { SettingsService } from '../../services/settings.service';
import { UIContext } from '../../services/ui-context';
import { AccountDetails, PaymentStateTypes, ResponsePipe } from '../../utils/request.utils';
import PortalLayout from './layout/portal-layout.component';
import PortalLoading from './layout/portal-loading.component';
import PortalPageHeader from './layout/portal-page-header.component';
import { longDate, planLabel } from './payments/checkout-format';
import {
    CANCELLED_LEGACY_TOAST,
    neverCompleted,
    notFoundAtProvider,
    CANCELLED_PAST_DUE,
    cancelled as cancelledMessage,
    cancelledActiveUntil,
    NOTHING_TO_CANCEL,
    SUSPENDED_LABEL,
    CANCELLED_NOW,
    cancelFailed,
    alreadyScheduledToEnd,
} from './payments/checkout-messages';
import { CancelModal, type CancelReason } from './plans/cancel-modal.component';
import styles from './portal.component.module.scss';

interface PortalComponentState {
    cancelModalOpen: boolean;
    /** `settings.featureFlags.cancelV2` (W0-9). `undefined` until settings arrive = today's behaviour. */
    cancelV2: boolean | undefined;
}

/** Unit tests inject stubs here; production never passes it (`@lazyInject` wires the real services). */
export interface PortalTestServices {
    loginService?: Pick<LoginService, 'isUserLoggedIn' | 'onUserDetailsChanged'>;
    http?: Pick<LightHttp, 'post'>;
    apiConfig?: Pick<ApiConfig, 'getCancelPaymentsEndpoint'>;
    uiContext?: Pick<UIContext, 'successToast' | 'handleError'>;
    settingsService?: Pick<SettingsService, 'featureFlags'>;
    offlineTaskService?: Pick<OfflineTaskService, 'getCanGoOfflineMode'>;
    scriptLoadService?: Pick<ScriptLoadService, 'loadAllScriptsIfNotLoaded'>;
}

interface PortalComponentProps {
    __testServices?: PortalTestServices;
}

/**
 * W0-9 (FIX-PLAN 2026-10-18): one cancelled subscription as the `cancelV2` response lists it
 * (`{cancelled:[{provider,id,plan,accessUntil,kind?}], nothingToCancel, message?}`; CONTRACT-REQUESTS CR L5→L4).
 */
interface CancelledItem {
    provider?: string | null;
    id?: string | null;
    plan?: string | null;
    accessUntil?: string | null;
    /** PERIOD_END (default) | NOW_VOIDED (past_due: cancelled now, invoice voided) | NEVER_COMPLETED (pending/404). */
    kind?: string | null;
    /** CR-L4-1: the backend's fine-grained outcome — CANCELLED | ALREADY_SCHEDULED | CANCELLED_NOW | CANCELLED_NOW_INVOICE_VOIDED | NEVER_COMPLETED | NOT_FOUND_AT_PROVIDER. */
    outcome?: string | null;
}

/**
 * W0-9: the toast is built from what the server says it cancelled, never from the click. A response without the
 * V2 fields (flag off, or an older server) yields today's text verbatim. Exported for the unit test (CN-05..CN-13).
 */
export function cancelToastFromResponse(resp: any): string {
    if (!resp || typeof resp !== 'object') return CANCELLED_LEGACY_TOAST;
    if (typeof resp.message === 'string' && resp.message.trim()) return resp.message;
    if (resp.nothingToCancel === true) return NOTHING_TO_CANCEL;
    if (!Array.isArray(resp.cancelled)) return CANCELLED_LEGACY_TOAST;
    const failed: Array<{provider?: string | null; id?: string | null}> = Array.isArray(resp.failed) ? resp.failed : [];
    if (resp.cancelled.length === 0 && failed.length === 0) return NOTHING_TO_CANCEL;
    const providerName = (p?: string | null) => p ? (p.toUpperCase() === 'PAYPAL' ? 'PayPal' : p.toUpperCase() === 'STRIPE' ? 'Stripe' : p) : 'subscription';
    const sentences = (resp.cancelled as CancelledItem[]).map((item) => {
        // CR-L4-1: the backend's `outcome` is authoritative; `kind` is its coarse mirror (CR-L5-3).
        const outcome = (item.outcome || '').toUpperCase();
        const kind = (item.kind || '').toUpperCase();
        const until = longDate(item.accessUntil) || 'the end of the paid period';
        if (outcome === 'ALREADY_SCHEDULED') return alreadyScheduledToEnd(until);
        if (outcome === 'CANCELLED_NOW_INVOICE_VOIDED' || kind === 'NOW_VOIDED') return CANCELLED_PAST_DUE;
        if (outcome === 'CANCELLED_NOW' || kind === 'NOW') return CANCELLED_NOW;
        // MON-13 (1): provider-aware — NEVER_COMPLETED is also a Stripe `incomplete` cancelled now; 404 is its own sentence
        const at = item.provider ? providerName(item.provider) : 'the provider';
        if (outcome === 'NOT_FOUND_AT_PROVIDER') return notFoundAtProvider(at);
        if (outcome === 'NEVER_COMPLETED' || kind === 'NEVER_COMPLETED') return neverCompleted(at);
        // MON-7: the server sends the display label since fix round 1; planLabel() is belt-and-braces for an older
        // server that still sends the storage constant (PACKAGE_TYPE_PRO) or a raw plan name (patron_monthly).
        return cancelledMessage(planLabel(item.plan) || 'your plan', providerName(item.provider), until);
    });
    failed.forEach((f) => sentences.push(cancelFailed(providerName(f.provider), f.id || '')));
    return sentences.join(' ');
}

/**
 * Portal landing page — "My Account" at `/portal`.
 *
 * Polished in Phase 7 to use the shared portal shell components
 * (header / loading / empty-state) and §7.0 design tokens. The cancel-
 * subscription button stays as-is — the rest of the page is presentation.
 */
export default class PortalComponent extends Component<PortalComponentProps, PortalComponentState> {
    userDetails: AccountDetails;
    loginViewMode: LoginDialogMode = 'login';
    ready = false;

    isPaidUser = false;

    state: PortalComponentState = {
        cancelModalOpen: false,
        cancelV2: undefined,
    };

    get isUserLoggedIn() {
        return this.loginService.isUserLoggedIn;
    }

    get canGoOffline() {
        return this.offlineTaskService.getCanGoOfflineMode();
    }

    // Cancelled subscriptions are active until end of billing cycle
    get isSubscriptionActive() {
        return this.isPaidUser;
    }

    /**
     * True when the user has explicitly cancelled a paid subscription.
     * The legacy User document uses `paymentState = "cancelled"` to mark
     * an active subscription that the user has cancelled but is still
     * within the grace period. `"unknown"` (the default for users who
     * never subscribed) and `"signup"` are NOT cancellations — they
     * just mean the user has no subscription to begin with.
     */
    get isPaymentCancelled() {
        return this.userDetails?.paymentState === PaymentStateTypes.cancelled;
    }

    /** True iff the user has never held a paid subscription. */
    get hasNeverSubscribed() {
        const ps = this.userDetails?.paymentState;
        return !this.isPaidUser
            && (ps === PaymentStateTypes.unknown
                || ps === PaymentStateTypes.signup
                || !ps);
    }

    get subscriptionName() {
        if (!this.userDetails) {
            return '';
        }
        if (this.userDetails.packageType === 'PACKAGE_TYPE_BASIC') {
            return 'Basic';
        } else if (this.userDetails.packageType === 'PACKAGE_TYPE_PRO') {
            return 'Pro';
        }
        return 'Free';
    }

    /**
     * W0-9 / NF4 (UT-09, CN-13): a suspended PayPal subscriber (V2 cache `WEBSITE` row `PAUSED`, legacy
     * `paid=false`) — today the page says "Inactive" and hides Cancel. Only consulted when `cancelV2` is on.
     */
    get isSuspended() {
        if (this.state.cancelV2 !== true) return false;
        return (this.userDetails?.entitlementsCache ?? []).some((e) => e?.type === 'WEBSITE' && e.state === 'PAUSED');
    }

    get subscriptionStatusReadable() {
        if (this.isPaymentCancelled && this.isSubscriptionActive) {
            // W0-10 / NF12 (UT-06, UT-07, UT-13): formatted date, never `Date.toString()` or "undefined".
            const until = longDate(this.userDetails.subscriptionNextBillingDate);
            return until ? cancelledActiveUntil(until) : 'Cancelled';
        }
        if (this.isPaymentCancelled) {
            return 'Cancelled';
        }
        if (this.isSubscriptionActive) {
            return 'Active';
        }
        if (this.isSuspended) {
            return SUSPENDED_LABEL;
        }
        if (this.hasNeverSubscribed) {
            return 'No subscription';
        }
        return 'Inactive';
    }

    @lazyInject(ApiConfig) private _apiConfig: ApiConfig;
    @lazyInject(LoginService) private _loginService: LoginService;
    @lazyInject(LightHttp) private _http: LightHttp;
    @lazyInject(OfflineTaskService) private _offlineTaskService: OfflineTaskService;
    @lazyInject(UIContext) private _uiContext: UIContext;
    @lazyInject(ScriptLoadService) private _scriptLoadService: ScriptLoadService;
    @lazyInject(SettingsService) private _settingsService: SettingsService;

    private get apiConfig() { return (this.props.__testServices?.apiConfig ?? this._apiConfig) as ApiConfig; }
    private get loginService() { return (this.props.__testServices?.loginService ?? this._loginService) as LoginService; }
    private get http() { return (this.props.__testServices?.http ?? this._http) as LightHttp; }
    private get offlineTaskService() { return (this.props.__testServices?.offlineTaskService ?? this._offlineTaskService) as OfflineTaskService; }
    private get uiContext() { return (this.props.__testServices?.uiContext ?? this._uiContext) as UIContext; }
    private get scriptLoadService() { return (this.props.__testServices?.scriptLoadService ?? this._scriptLoadService) as ScriptLoadService; }
    private get settingsService() { return (this.props.__testServices?.settingsService ?? this._settingsService) as SettingsService | undefined; }

    constructor(props) {
        super(props);
        this.getUserDetails();
        this.scriptLoadService.loadAllScriptsIfNotLoaded(true);
    }

    componentDidMount() {
        // featureFlags is a ReplaySubject(1) — emits immediately once settings loaded. `undefined` until then.
        this.settingsService?.featureFlags?.subscribe({
            next: (ff) => this.setState({cancelV2: ff?.cancelV2 === true}),
        });
    }

    updateUserDetails(mode: string) {
        if (mode === 'register') {
            this.loginViewMode = 'register';
        }
        if (this.loginService.isUserLoggedIn) {
            this.getUserDetails();
        }
    }

    getUserDetails() {
        this.loginService.onUserDetailsChanged.subscribe(
            data => {
                this.userDetails = data;
                this.ready = true;
                if (data) {
                    this.isPaidUser = this.userDetails.isPaidUser();
                }
                this.forceUpdate();
            },
            (err) => {
                console.error(err);
                this.ready = true;
                this.forceUpdate();
            },
        );
    }

    cancelSubscription() {
        this.setState({cancelModalOpen: true});
    }

    private closeCancelModal = (): void => {
        this.setState({cancelModalOpen: false});
    };

    private onCancelConfirm = ({reason}: {reason: CancelReason | null}): void => {
        this.http.post(this.apiConfig.getCancelPaymentsEndpoint(), {reason}).pipe(
            ResponsePipe,
        ).subscribe({
            next: (resp) => {
                // W0-9: the toast states what the server cancelled (per item, with access-until) or that there was
                // nothing to cancel; a legacy response (`{state:'success'}`) keeps today's text byte for byte.
                this.uiContext.successToast(cancelToastFromResponse(resp));
                this.setState({cancelModalOpen: false});
                setTimeout(() => location.reload(), 2000);
            },
            error: (err) => {
                this.uiContext.handleError(err);
                this.setState({cancelModalOpen: false});
                this.forceUpdate();
            },
        });
    };

    private getCancelModalActiveUntil(): string | null {
        const next = this.userDetails?.subscriptionNextBillingDate;
        if (!next) return null;
        try {
            return next instanceof Date ? next.toISOString() : String(next);
        } catch {
            return null;
        }
    }

    private subscriptionStateClass(): string {
        if (this.isPaymentCancelled && this.isSubscriptionActive) return styles.stateCancelled;
        if (this.isPaymentCancelled) return styles.stateCancelled;
        if (this.isSubscriptionActive) return styles.stateActive;
        return styles.stateInactive;
    }

    private renderLoggedInBody() {
        const header = (
            <PortalPageHeader
                title="My Account"
                subtitle="Profile details, plan, and subscription management."
                breadcrumbs={[{label: 'Portal'}, {label: 'My Account'}]}
            />
        );

        return (
            <>
                {header}
                <div className={styles.pageBody}>
                    <section className={styles.section}>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <h2 className={styles.cardTitle}>Profile</h2>
                            </div>
                            <div className={styles.fieldRow}>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>Name</span>
                                    <span className={styles.fieldValue}>
                                        {this.userDetails?.name || 'Not provided'}
                                    </span>
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>Email</span>
                                    <span className={styles.fieldValue}>
                                        {this.userDetails?.email || '—'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className={styles.section}>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <h2 className={styles.cardTitle}>Subscription</h2>
                            </div>
                            <div className={styles.fieldRow}>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>Plan</span>
                                    <span className={styles.fieldValue}>
                                        {this.subscriptionName} plan
                                    </span>
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>Status</span>
                                    <span
                                        className={`${styles.statePill} ${this.subscriptionStateClass()}`}
                                    >
                                        {this.subscriptionStatusReadable}
                                    </span>
                                </div>
                            </div>

                            {!this.canGoOffline && (
                                <div
                                    className={styles.upgradeCallout}
                                    style={{marginTop: 20}}
                                >
                                    <div className={styles.upgradeCopy}>
                                        <h3>You're on the Free tier</h3>
                                        <p>
                                            Upgrade to unlock higher limits, the API,
                                            and monthly credit grants.
                                        </p>
                                    </div>
                                    <a className={styles.upgradeBtn} href="/product">
                                        View plans
                                    </a>
                                </div>
                            )}

                            {((!this.isPaymentCancelled && this.isSubscriptionActive) || this.isSuspended) && (
                                <div className={styles.cancelCard}>
                                    <div className={styles.cancelCopy}>
                                        <p className={styles.cancelTitle}>
                                            Cancel subscription
                                        </p>
                                        <p className={styles.cancelHint}>
                                            Access continues until the end of your
                                            billing cycle.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.cancelBtn}
                                        onClick={() => this.cancelSubscription()}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    </section>

                    <div className={styles.supportFootnote}>
                        Need help?{' '}
                        <a href="mailto:support@xconvert.com">
                            support@xconvert.com
                        </a>
                    </div>
                </div>
            </>
        );
    }

    private renderLoggedOutBody() {
        return (
            <>
                <PortalPageHeader
                    title="Welcome back"
                    subtitle="Sign in to manage your account, plan, and developer credentials."
                />
                <div className={styles.pageBody}>
                    <div className={styles.loginCard}>
                        <div className={styles.loginHeading}>
                            <h2>Sign in</h2>
                            <p>Use your xconvert email and password to continue.</p>
                        </div>
                        <div className={styles.loginWrapper}>
                            <LoginComponent
                                passwordResetEnabled={true}
                                registerEnabled={true}
                                submitBtnText={'Log In'}
                                mode={this.loginViewMode}
                                onSuccess={($event) => this.updateUserDetails($event)}
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    }

    getHTML() {
        if (this.isUserLoggedIn) {
            return this.renderLoggedInBody();
        }
        if (!this.isUserLoggedIn && this.ready) {
            return this.renderLoggedOutBody();
        }
        return (
            <>
                <PortalPageHeader title="Loading…" />
                <PortalLoading message="Loading your account…" />
            </>
        );
    }

    render() {
        return (
            <PortalLayout activePath="/portal">
                {this.getHTML()}
                {this.state.cancelModalOpen && (
                    <CancelModal
                        open
                        productLabel={this.subscriptionName || 'subscription'}
                        productSummary="Subscription benefits"
                        activeUntil={this.getCancelModalActiveUntil()}
                        onKeep={this.closeCancelModal}
                        onConfirm={this.onCancelConfirm}
                    />
                )}
            </PortalLayout>
        );
    }
}
